import React, { ReactElement, useEffect, useState } from "react";
import REIToggablePanel from "../../../components/ui/toggablePanel";
import AnalysisVariables from "./Child/AnalysisVariables";
import SummaryInput from "./Child/SummaryInput";
import AnalysisOverview from "./Child/AnalysisOverview";
import { REIButton } from "../../../components/ui";
import { MEMBER_KEY, USER_DETAIL, YEARS } from "../../../utils/constants";
import Statistics from "./Child/Statistics";
import NonCashDeduction from "./Child/NonCashDeduction";
import OverviewSummary from "./Child/OverviewSummary";
import CashDeductions from "./Child/CashDeductions";
import { IAnalysisSummaryResponse } from "../../../interface/analysisSummary";
import { RootState } from "../../../store";
import { useSelector } from "react-redux";
import { ApiEndPoints } from "../../../utils/endpoints";
import { useHttpGets } from "../../../services/httpService";
import { REIProgressSpinner } from "../../../components/ui";

interface IAnalysisProps {
  loadFromPortfolioTracker?: boolean;
  isEditable?: boolean;
}

const Analysis: React.FC<IAnalysisProps> = ({ loadFromPortfolioTracker = false, isEditable = false }) => {
  const [editFlag, setEditFlag] = React.useState(false);
  const [showAllColumns, setShowAllColumns] = useState(false);
  const [visibleYears, setVisibleYears] = useState<{ label: string; defaultShow: boolean }[]>([]);
  const analysisSummary = useSelector((state: RootState) => state.analyser.analysisSummary as IAnalysisSummaryResponse);
  const memberDetail = JSON.parse(localStorage.getItem(USER_DETAIL) || "{}");
  const [countryCode, setCountryCode] = useState<string>(memberDetail.Country);
  const hasAnalysisStepData =
    !!analysisSummary &&
    !!analysisSummary.SummaryInput &&
    !!analysisSummary.AnalysisOverview &&
    !!analysisSummary.AnalysisStatistics &&
    !!analysisSummary.AnalysisCashDeduction &&
    !!analysisSummary.AnalysisNonCashDeduction &&
    !!analysisSummary.AnalysisSummary &&
    Array.isArray(analysisSummary.PropertyRentalIncome) &&
    Array.isArray(analysisSummary.PropertyExpense) &&
    Array.isArray(analysisSummary.LoanPrincipalInterest) &&
    Array.isArray(analysisSummary.ForecastOverrides);

  useEffect(() => {
    let url = "";
    if (
      analysisSummary == null ||
      (analysisSummary.PropertyAnalyserId == null && loadFromPortfolioTracker == false) ||
      (analysisSummary.PortfolioTrackerId == null && loadFromPortfolioTracker == true)
    ) {
      return;
    }

    if (!loadFromPortfolioTracker) {
      url = `${ApiEndPoints.analyser}/PropertyDetailSummary?MemberId=${localStorage.getItem(MEMBER_KEY)}&PropertyAnalyserId=${analysisSummary?.PropertyAnalyserId}&MemberCountryCode=${memberDetail.Country}`;
    } else {
      url = `${ApiEndPoints.tracker}/PortfolioTracker/Tracker?MemberId=${localStorage.getItem(MEMBER_KEY)}&PortfolioTrackerId=${analysisSummary?.PortfolioTrackerId}&userCountry=${memberDetail.Country}`;
    }
    useHttpGets(url, undefined)
      .then((result) => {
        if (result.status === 200 && result.data) {
          if (loadFromPortfolioTracker) {
            setCountryCode((result.data as any).CountryCode);
          } else {
            setCountryCode((result.data as any).PropertyDetailSummary?.Country == "NZ" ? "2" : "1");
          }
          resetEditFlag();
        }
      })
      .catch(() => {});
  }, [loadFromPortfolioTracker, analysisSummary]);

  useEffect(() => {
    setVisibleYears(showAllColumns ? YEARS : YEARS.filter((year) => year.defaultShow));
  }, [showAllColumns]);

  const resetEditFlag = () => {
    setEditFlag(false);
  };

  const reloadEditableDataTable = () => {
    setEditFlag(false);
    setTimeout(() => {
      setEditFlag(true);
    }, 2000);
  };

  const headerTemplate = (options: any): ReactElement<any, any> => {
    const className = `${options.className} justify-content-space-between`;

    return (
      <div className={`${className} bg-slate-600 `}>
        <div className="flex align-items-center gap-2">
          <REIButton
            id="anal-btn-toggleyears"
            icon=""
            label={!showAllColumns ? "Show all years(0-10)" : "Show only years 1,3,5 & 10"}
            classNames="text-white"
            onClick={() => setShowAllColumns(!showAllColumns)}
          />
        </div>
        <div>
          <REIButton
            id="anal-btn-editforecast"
            icon="pi pi-user-edit"
            label={!editFlag ? "Edit future years forecast" : "Stop forecast editing"}
            classNames="p-button mr-2"
            onClick={() => setEditFlag(!editFlag)}
          />
          {options.togglerElement}
        </div>
      </div>
    );
  };

  const renderHeaderAndAnalysisOverview = () => (
    <>
      <div className="py-0">
        <table className="w-full text-left border-solid border border-x-0 border-y-0 border-b border-gray-300">
          <thead>
            <tr>
              <th className="border-b p-2"></th>
              {visibleYears.map((year) => (
                <th key={year.label} className="border-b p-2 text-end w-36 text-blue-600">
                  {year.label}
                </th>
              ))}
            </tr>
          </thead>
        </table>
      </div>
      <AnalysisOverview
        editFlag={editFlag}
        visibleYears={visibleYears}
        resetEditFlag={resetEditFlag}
        isfromPortfolioTracker={loadFromPortfolioTracker}
        reloadEditableDataTable={reloadEditableDataTable}
      />
      <Statistics
        editFlag={editFlag}
        visibleYears={visibleYears}
        resetEditFlag={resetEditFlag}
        isfromPortfolioTracker={loadFromPortfolioTracker}
        reloadEditableDataTable={reloadEditableDataTable}
      />
      <CashDeductions
        editFlag={editFlag}
        visibleYears={visibleYears}
        resetEditFlag={resetEditFlag}
        isfromPortfolioTracker={loadFromPortfolioTracker}
        reloadEditableDataTable={reloadEditableDataTable}
      />
      <NonCashDeduction
        editFlag={editFlag}
        visibleYears={visibleYears}
        resetEditFlag={resetEditFlag}
        isfromPortfolioTracker={loadFromPortfolioTracker}
        reloadEditableDataTable={reloadEditableDataTable}
      />
      <OverviewSummary
        editFlag={editFlag}
        visibleYears={visibleYears}
        resetEditFlag={resetEditFlag}
        isfromPortfolioTracker={loadFromPortfolioTracker}
        reloadEditableDataTable={reloadEditableDataTable}
        countryCode={countryCode}
      />
    </>
  );

  if (!hasAnalysisStepData) {
    return (
      <div className="w-full text-center">
        <REIProgressSpinner />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col lg:flex-row flex-wrap gap-2">
        <div className="flex flex-col lg:flex-row lg:w-full gap-2">
          <div className="flex-0 lg:w-[40%] w-full" id="anal-pnl-variables" data-testid="anal-pnl-variables">
            <REIToggablePanel
              header="Variables"
              istoggleable={false}
              defaultCollapse={false}
              contentComponent={<AnalysisVariables isfromPortfolioTracker={loadFromPortfolioTracker} />}
            />
          </div>
          <div className="flex-0 lg:w-[60%] w-full" id="anal-pnl-summaryinputs" data-testid="anal-pnl-summaryinputs">
            <REIToggablePanel
              header="Summary Inputs"
              istoggleable={false}
              defaultCollapse={false}
              customClassName="summary-input-panel"
              contentComponent={
                <SummaryInput isfromPortfolioTracker={loadFromPortfolioTracker} isEditable={isEditable} />
              }
            />
          </div>
        </div>
        <div className="w-full" id="anal-pnl-analysis" data-testid="anal-pnl-analysis">
          <REIToggablePanel
            header="Show all years(0-10)"
            istoggleable={false}
            defaultCollapse={false}
            customHeader={headerTemplate}
            contentComponent={renderHeaderAndAnalysisOverview()}
          />
        </div>
      </div>
    </>
  );
};

export default Analysis;
